import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../../ui/alert-dialog';
import {
  Shield,
  Calendar,
  FileText,
  Search,
  MoreVertical,
  Eye,
  CheckCircle,
  AlertTriangle,
  ListChecks,
  MapPin,
  Save,
  Download,
  Loader2,
  Trash2
} from 'lucide-react';
import { projectService } from '../../../src/services/project.service';
import { showSuccess, showError } from '../../../src/utils/notification.utils';

function NewInspectionModal({ onSuccess }: { onSuccess?: () => void }) {
  const [open, setOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [projects, setProjects] = useState<any[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    project: '',
    inspectionType: '',
    priority: 'medium',
    description: '',
    requestedBy: 'Current User',
    inspector: '',
    inspectorContact: '',
    scheduledDate: '',
    location: '',
    workArea: '',
    contractor: '',
    phase: '',
    requirements: '',
    safetyRequirements: '',
    equipmentNeeded: '',
    notes: ''
  });

  const [errors, setErrors] = useState<{[key: string]: string}>({});

  // Fetch projects when modal opens
  useEffect(() => {
    if (open) {
      loadProjects();
    }
  }, [open]);

  const loadProjects = async () => {
    setLoadingProjects(true);
    try {
      const response = await projectService.getProjects();
      console.log('Inspection Modal - Projects API Response:', response);
      if (response.success && response.data) {
        const projectData = Array.isArray(response.data)
          ? response.data
          : ((response.data as any).data || []);
        console.log('Inspection Modal - Setting projects:', projectData);
        setProjects(projectData);
      }
    } catch (error) {
      console.error('Error loading projects:', error);
      showError({ title: 'Error', message: 'Failed to load projects' });
    } finally {
      setLoadingProjects(false);
    }
  };

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Inspection title is required';
    }
    if (!formData.project) {
      newErrors.project = 'Project selection is required';
    }
    if (!formData.inspectionType) {
      newErrors.inspectionType = 'Inspection type is required';
    }
    if (!formData.scheduledDate) {
      newErrors.scheduledDate = 'Scheduled date is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);

    try {
      const { inspectionService } = await import('../../../src/services/inspection.service');
      const { authService } = await import('../../../src/services/auth.service');

      const userProfile = await authService.getUserProfile();
      const orgId = userProfile?.organizationId;

      if (!orgId) {
        showError({ title: 'Error', message: 'Organization ID not found' });
        setIsSaving(false);
        return;
      }

      // Get next inspection number
      const inspectionNumberResponse = await inspectionService.getNextInspectionNumber(formData.project);
      if (!inspectionNumberResponse.success || !inspectionNumberResponse.data) {
        showError({ title: 'Error', message: 'Failed to generate inspection number' });
        setIsSaving(false);
        return;
      }

      const inspectionData = {
        organization_id: orgId,
        project_id: formData.project,
        inspection_number: inspectionNumberResponse.data.inspection_number,
        inspection_type: formData.inspectionType,
        title: formData.title,
        description: formData.description || undefined,
        scheduled_date: new Date(formData.scheduledDate).toISOString(),
        inspector_name: formData.inspector || undefined,
        inspector_contact: formData.inspectorContact || undefined,
        location: formData.location || undefined,
        work_area: formData.workArea || undefined,
        contractor: formData.contractor || undefined,
        phase: formData.phase || undefined,
        requirements: formData.requirements || undefined,
        safety_requirements: formData.safetyRequirements || undefined,
        equipment_needed: formData.equipmentNeeded || undefined,
        priority: formData.priority || undefined,
        notes: formData.notes || undefined,
        status: 'scheduled' as const
      };

      const response = await inspectionService.createInspection(inspectionData);

      if (response.success && response.data) {
        showSuccess({ title: 'Success', message: 'Inspection scheduled successfully' });
        setOpen(false);
        // Reset form
        setFormData({
          title: '',
          project: '',
          inspectionType: '',
          priority: 'medium',
          description: '',
          requestedBy: 'Current User',
          inspector: '',
          inspectorContact: '',
          scheduledDate: '',
          location: '',
          workArea: '',
          contractor: '',
          phase: '',
          requirements: '',
          safetyRequirements: '',
          equipmentNeeded: '',
          notes: ''
        });
        setErrors({});
        // Reload inspection table
        onSuccess?.();
      } else {
        showError({ title: 'Error', message: 'Failed to schedule inspection' });
      }
    } catch (error: any) {
      console.error('Error scheduling inspection:', error);
      showError({ title: 'Error', message: error.message || 'Failed to schedule inspection' });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Shield className="h-4 w-4 mr-2" />
          Schedule Inspection
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto modal-content">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Schedule New Inspection
          </DialogTitle>
          <DialogDescription>
            Schedule quality inspection for construction work
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Basic Information */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Inspection Information</h4>
            </div>

            <div className="space-y-2">
              <Label htmlFor="inspection-title">Inspection Title *</Label>
              <Input
                id="inspection-title"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder="e.g., Foundation Concrete Pour Inspection"
              />
              {errors.title && <p className="text-sm text-destructive">{errors.title}</p>}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="inspection-project">Project *</Label>
                <Select value={formData.project} onValueChange={(value) => setFormData({...formData, project: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select project" />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map((project) => (
                      <SelectItem key={project.project_id} value={project.project_id}>
                        {project.project_name || project.name || 'Unnamed Project'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.project && <p className="text-sm text-destructive">{errors.project}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="inspection-type">Inspection Type *</Label>
                <Select value={formData.inspectionType} onValueChange={(value) => setFormData({...formData, inspectionType: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pre-pour">Pre-Pour Inspection</SelectItem>
                    <SelectItem value="concrete-strength">Concrete Strength Test</SelectItem>
                    <SelectItem value="steel-inspection">Steel Work Inspection</SelectItem>
                    <SelectItem value="electrical">Electrical Systems</SelectItem>
                    <SelectItem value="plumbing">Plumbing Systems</SelectItem>
                    <SelectItem value="hvac">HVAC Systems</SelectItem>
                    <SelectItem value="fire-safety">Fire Safety Systems</SelectItem>
                    <SelectItem value="structural">Structural Inspection</SelectItem>
                    <SelectItem value="final-walkthrough">Final Walkthrough</SelectItem>
                    <SelectItem value="building-envelope">Building Envelope</SelectItem>
                    <SelectItem value="site-work">Site Work</SelectItem>
                    <SelectItem value="specialty">Specialty Inspection</SelectItem>
                  </SelectContent>
                </Select>
                {errors.inspectionType && <p className="text-sm text-destructive">{errors.inspectionType}</p>}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="inspection-description">Description</Label>
              <Textarea
                id="inspection-description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Detailed description of work to be inspected..."
                rows={3}
              />
            </div>
          </div>

          {/* Schedule & Assignment */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Schedule & Assignment</h4>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="inspector">Inspector Name</Label>
                  <Input
                    id="inspector"
                    value={formData.inspector}
                    onChange={(e) => setFormData({...formData, inspector: e.target.value})}
                    placeholder="e.g., John Doe"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="inspector-contact">Inspector Contact</Label>
                  <Input
                    id="inspector-contact"
                    value={formData.inspectorContact}
                    onChange={(e) => setFormData({...formData, inspectorContact: e.target.value})}
                    placeholder="e.g., john.doe@example.com or (555) 123-4567"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="scheduled-date">Scheduled Date & Time *</Label>
              <Input
                id="scheduled-date"
                type="datetime-local"
                value={formData.scheduledDate}
                onChange={(e) => setFormData({...formData, scheduledDate: e.target.value})}
              />
              {errors.scheduledDate && <p className="text-sm text-destructive">{errors.scheduledDate}</p>}
            </div>
          </div>

          {/* Location & Work Details */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Location & Work Details</h4>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="inspection-location">Location</Label>
                <Input
                  id="inspection-location"
                  value={formData.location}
                  onChange={(e) => setFormData({...formData, location: e.target.value})}
                  placeholder="e.g., Building A - Ground Floor"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="work-area">Work Area/Zone</Label>
                <Input
                  id="work-area"
                  value={formData.workArea}
                  onChange={(e) => setFormData({...formData, workArea: e.target.value})}
                  placeholder="e.g., Foundation Area, Grid A-C, 1-5"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contractor">Contractor/Subcontractor</Label>
                <Select value={formData.contractor} onValueChange={(value) => setFormData({...formData, contractor: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select contractor" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="main-contractor">Main Contractor</SelectItem>
                    <SelectItem value="concrete-sub">Concrete Subcontractor</SelectItem>
                    <SelectItem value="steel-sub">Steel Subcontractor</SelectItem>
                    <SelectItem value="electrical-sub">Electrical Subcontractor</SelectItem>
                    <SelectItem value="plumbing-sub">Plumbing Subcontractor</SelectItem>
                    <SelectItem value="hvac-sub">HVAC Subcontractor</SelectItem>
                    <SelectItem value="specialty-sub">Specialty Contractor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="project-phase">Project Phase</Label>
                <Select value={formData.phase} onValueChange={(value) => setFormData({...formData, phase: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select phase" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="foundation">Foundation</SelectItem>
                    <SelectItem value="structure">Structure</SelectItem>
                    <SelectItem value="envelope">Building Envelope</SelectItem>
                    <SelectItem value="mep-rough">MEP Rough-In</SelectItem>
                    <SelectItem value="interior">Interior Fit-Out</SelectItem>
                    <SelectItem value="exterior">Exterior Finish</SelectItem>
                    <SelectItem value="final">Final Completion</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Requirements & Preparation */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <ListChecks className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Requirements & Preparation</h4>
            </div>

            <div className="space-y-2">
              <Label htmlFor="requirements">Inspection Requirements</Label>
              <Textarea
                id="requirements"
                value={formData.requirements}
                onChange={(e) => setFormData({...formData, requirements: e.target.value})}
                placeholder="Specific requirements, standards, or criteria for this inspection..."
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="safety-requirements">Safety Requirements</Label>
              <Textarea
                id="safety-requirements"
                value={formData.safetyRequirements}
                onChange={(e) => setFormData({...formData, safetyRequirements: e.target.value})}
                placeholder="Safety protocols, PPE requirements, hazard considerations..."
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="equipment-needed">Equipment/Tools Needed</Label>
              <Input
                id="equipment-needed"
                value={formData.equipmentNeeded}
                onChange={(e) => setFormData({...formData, equipmentNeeded: e.target.value})}
                placeholder="e.g., Testing equipment, measuring tools, access equipment"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="inspection-notes">Additional Notes</Label>
              <Textarea
                id="inspection-notes"
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Any additional information or special instructions..."
                rows={2}
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Scheduling...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Schedule Inspection
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// View Inspection Details Modal Component
function ViewInspectionModal({ inspection, trigger }: { inspection: any; trigger: React.ReactNode }) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        {trigger}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto modal-content">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5" />
            Inspection Details
          </DialogTitle>
          <DialogDescription>
            View complete inspection information
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium">Inspection ID</Label>
              <p className="text-sm text-muted-foreground font-mono">{inspection.inspection_number || inspection.inspection_id}</p>
            </div>
            <div>
              <Label className="text-sm font-medium">Status</Label>
              <p className="text-sm text-muted-foreground">{inspection.status}</p>
            </div>
          </div>

          <div>
            <Label className="text-sm font-medium">Title</Label>
            <p className="text-sm text-muted-foreground">{inspection.title}</p>
          </div>

          {inspection.description && (
            <div>
              <Label className="text-sm font-medium">Description</Label>
              <p className="text-sm text-muted-foreground">{inspection.description}</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium">Type</Label>
              <p className="text-sm text-muted-foreground">{inspection.inspection_type}</p>
            </div>
            <div>
              <Label className="text-sm font-medium">Priority</Label>
              <p className="text-sm text-muted-foreground">{inspection.priority || 'N/A'}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium">Scheduled Date</Label>
              <p className="text-sm text-muted-foreground">
                {new Date(inspection.scheduled_date).toLocaleString()}
              </p>
            </div>
            {inspection.actual_date && (
              <div>
                <Label className="text-sm font-medium">Actual Date</Label>
                <p className="text-sm text-muted-foreground">
                  {new Date(inspection.actual_date).toLocaleString()}
                </p>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium">Inspector</Label>
              <p className="text-sm text-muted-foreground">{inspection.inspector_name || 'Not assigned'}</p>
            </div>
            <div>
              <Label className="text-sm font-medium">Contact</Label>
              <p className="text-sm text-muted-foreground">{inspection.inspector_contact || 'N/A'}</p>
            </div>
          </div>

          {inspection.location && (
            <div>
              <Label className="text-sm font-medium">Location</Label>
              <p className="text-sm text-muted-foreground">{inspection.location}</p>
            </div>
          )}

          {inspection.work_area && (
            <div>
              <Label className="text-sm font-medium">Work Area</Label>
              <p className="text-sm text-muted-foreground">{inspection.work_area}</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            {inspection.contractor && (
              <div>
                <Label className="text-sm font-medium">Contractor</Label>
                <p className="text-sm text-muted-foreground">{inspection.contractor}</p>
              </div>
            )}
            {inspection.phase && (
              <div>
                <Label className="text-sm font-medium">Phase</Label>
                <p className="text-sm text-muted-foreground">{inspection.phase}</p>
              </div>
            )}
          </div>

          {inspection.requirements && (
            <div>
              <Label className="text-sm font-medium">Requirements</Label>
              <p className="text-sm text-muted-foreground">{inspection.requirements}</p>
            </div>
          )}

          {inspection.safety_requirements && (
            <div>
              <Label className="text-sm font-medium">Safety Requirements</Label>
              <p className="text-sm text-muted-foreground">{inspection.safety_requirements}</p>
            </div>
          )}

          {inspection.equipment_needed && (
            <div>
              <Label className="text-sm font-medium">Equipment Needed</Label>
              <p className="text-sm text-muted-foreground">{inspection.equipment_needed}</p>
            </div>
          )}

          {inspection.result && (
            <div>
              <Label className="text-sm font-medium">Result</Label>
              <p className="text-sm text-muted-foreground">{inspection.result}</p>
            </div>
          )}

          {inspection.required_actions && (
            <div>
              <Label className="text-sm font-medium">Required Actions</Label>
              <p className="text-sm text-muted-foreground">{inspection.required_actions}</p>
            </div>
          )}

          {inspection.reinspection_date && (
            <div>
              <Label className="text-sm font-medium">Reinspection Date</Label>
              <p className="text-sm text-muted-foreground">
                {new Date(inspection.reinspection_date).toLocaleDateString()}
              </p>
            </div>
          )}

          {inspection.notes && (
            <div>
              <Label className="text-sm font-medium">Notes</Label>
              <p className="text-sm text-muted-foreground">{inspection.notes}</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Inspection Report Modal Component
function InspectionReportModal({ inspection, trigger, onSuccess }: { inspection: any; trigger: React.ReactNode; onSuccess?: () => void }) {
  const [open, setOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [reportData, setReportData] = useState({
    status: 'passed',
    findings: '',
    deficiencies: '',
    recommendations: '',
    followUpRequired: false,
    followUpDate: '',
    certificationIssued: false,
    photos: [] as string[],
    inspectionDate: new Date().toISOString().split('T')[0],
    inspectedBy: 'Current User'
  });

  const handleSubmitReport = async () => {
    setIsSaving(true);
    try {
      const { inspectionService } = await import('../../../src/services/inspection.service');

      const passed = reportData.status === 'passed' || reportData.status === 'passed-with-conditions';
      const result = reportData.findings + (reportData.recommendations ? '\n\nRecommendations:\n' + reportData.recommendations : '');
      const requiredActions = reportData.deficiencies || undefined;
      const reinspectionDate = reportData.followUpRequired ? reportData.followUpDate : undefined;

      const response = await inspectionService.completeInspection(
        inspection.inspection_id,
        passed,
        result,
        requiredActions,
        reinspectionDate
      );

      if (response.success) {
        showSuccess({ title: 'Success', message: 'Inspection report submitted successfully' });
        setOpen(false);
        onSuccess?.();
      } else {
        showError({ title: 'Error', message: 'Failed to submit inspection report' });
      }
    } catch (error: any) {
      console.error('Error submitting inspection report:', error);
      showError({ title: 'Error', message: error.message || 'Failed to submit inspection report' });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto modal-content">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Inspection Report
          </DialogTitle>
          <DialogDescription>
            Complete inspection report for: {inspection.title}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <div className="p-4 bg-muted/50 rounded-lg">
            <h4 className="font-medium mb-2">Inspection Details</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium">Type:</span>
                <p className="text-muted-foreground">{inspection.inspection_type}</p>
              </div>
              <div>
                <span className="font-medium">Location:</span>
                <p className="text-muted-foreground">{inspection.location || 'N/A'}</p>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="inspection-status">Inspection Result</Label>
            <Select value={reportData.status} onValueChange={(value) => setReportData({...reportData, status: value})}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="passed">Passed</SelectItem>
                <SelectItem value="passed-with-conditions">Passed with Conditions</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="incomplete">Incomplete</SelectItem>
                <SelectItem value="postponed">Postponed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="findings">Findings</Label>
            <Textarea
              id="findings"
              value={reportData.findings}
              onChange={(e) => setReportData({...reportData, findings: e.target.value})}
              placeholder="Document inspection findings, observations, and test results..."
              rows={4}
            />
          </div>

          {(reportData.status === 'failed' || reportData.status === 'passed-with-conditions') && (
            <div className="space-y-2">
              <Label htmlFor="deficiencies">Deficiencies & Non-Compliance Issues</Label>
              <Textarea
                id="deficiencies"
                value={reportData.deficiencies}
                onChange={(e) => setReportData({...reportData, deficiencies: e.target.value})}
                placeholder="List any deficiencies, non-compliance issues, or areas requiring correction..."
                rows={3}
              />
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="recommendations">Recommendations</Label>
            <Textarea
              id="recommendations"
              value={reportData.recommendations}
              onChange={(e) => setReportData({...reportData, recommendations: e.target.value})}
              placeholder="Provide recommendations for improvement or next steps..."
              rows={3}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="follow-up-required"
                checked={reportData.followUpRequired}
                onChange={(e) => setReportData({...reportData, followUpRequired: e.target.checked})}
                className="rounded border-border"
              />
              <Label htmlFor="follow-up-required">Follow-up Inspection Required</Label>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="certification-issued"
                checked={reportData.certificationIssued}
                onChange={(e) => setReportData({...reportData, certificationIssued: e.target.checked})}
                className="rounded border-border"
              />
              <Label htmlFor="certification-issued">Certification Issued</Label>
            </div>
          </div>

          {reportData.followUpRequired && (
            <div className="space-y-2">
              <Label htmlFor="follow-up-date">Follow-up Inspection Date</Label>
              <Input
                id="follow-up-date"
                type="date"
                value={reportData.followUpDate}
                onChange={(e) => setReportData({...reportData, followUpDate: e.target.value})}
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={isSaving}>Cancel</Button>
          <Button onClick={handleSubmitReport} disabled={isSaving}>
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <CheckCircle className="h-4 w-4 mr-2" />
                Submit Report
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Reschedule Inspection Modal Component
function RescheduleInspectionModal({ inspection, trigger, onSuccess }: { inspection: any; trigger: React.ReactNode; onSuccess?: () => void }) {
  const [open, setOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [scheduledDate, setScheduledDate] = useState(
    inspection.scheduled_date ? new Date(inspection.scheduled_date).toISOString().slice(0, 16) : ''
  );
  const [notes, setNotes] = useState('');

  const handleReschedule = async () => {
    if (!scheduledDate) {
      showError({ title: 'Validation Error', message: 'Please select a new date and time' });
      return;
    }

    setIsSaving(true);
    try {
      const { inspectionService } = await import('../../../src/services/inspection.service');

      const response = await inspectionService.updateInspection(inspection.inspection_id, {
        scheduled_date: new Date(scheduledDate).toISOString(),
        notes: notes || undefined,
        status: 'scheduled'
      });

      if (response.success) {
        showSuccess({ title: 'Success', message: 'Inspection rescheduled successfully' });
        setOpen(false);
        onSuccess?.();
      } else {
        showError({ title: 'Error', message: 'Failed to reschedule inspection' });
      }
    } catch (error: any) {
      console.error('Error rescheduling inspection:', error);
      showError({ title: 'Error', message: error.message || 'Failed to reschedule inspection' });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px] modal-content">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Reschedule Inspection
          </DialogTitle>
          <DialogDescription>
            Update the scheduled date and time for: {inspection.title}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="p-3 bg-muted/50 rounded-lg">
            <p className="text-sm font-medium">Current Schedule</p>
            <p className="text-sm text-muted-foreground">
              {new Date(inspection.scheduled_date).toLocaleString()}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="new-scheduled-date">New Date & Time *</Label>
            <Input
              id="new-scheduled-date"
              type="datetime-local"
              value={scheduledDate}
              onChange={(e) => setScheduledDate(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="reschedule-notes">Reason for Rescheduling</Label>
            <Textarea
              id="reschedule-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional: Provide reason for rescheduling..."
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleReschedule} disabled={isSaving}>
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Rescheduling...
              </>
            ) : (
              <>
                <Calendar className="h-4 w-4 mr-2" />
                Reschedule
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Inspection Actions Menu Component
function InspectionActionsMenu({ inspection, onSuccess, projects }: { inspection: any; onSuccess?: () => void; projects?: any[] }) {
  const handleExportPDF = async () => {
    try {
      const { pdfUtils } = await import('../../../src/utils/pdf.utils');

      const getProjectName = (projectId: string) => {
        const project = projects?.find(p => p.project_id === projectId);
        return project?.project_name || project?.name || 'Unknown Project';
      };

      const formatDate = (dateString: string) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
      };

      const formatTime = (dateString: string) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
      };

      const formatInspectionType = (type: string) => {
        return type?.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || '';
      };

      // Create PDF with branding
      const doc = pdfUtils.createPDF({
        title: 'Inspection Report',
        subtitle: inspection.title,
        documentNumber: inspection.inspection_number || inspection.inspection_id,
        date: formatDate(new Date().toISOString()),
        footer: `Generated by OptiBuild - Confidential`
      });

      let y = 70;

      // Basic Information Section
      y = pdfUtils.addSectionHeader(doc, 'Inspection Information', y);
      y = pdfUtils.addKeyValueGrid(doc, [
        { label: 'Inspection Number', value: inspection.inspection_number || inspection.inspection_id },
        { label: 'Title', value: inspection.title || 'N/A' },
        { label: 'Project', value: getProjectName(inspection.project_id) },
        { label: 'Status', value: inspection.status || 'N/A' },
        { label: 'Type', value: formatInspectionType(inspection.inspection_type) },
        { label: 'Priority', value: inspection.priority || 'N/A' },
        { label: 'Scheduled Date', value: `${formatDate(inspection.scheduled_date)} at ${formatTime(inspection.scheduled_date)}` },
        { label: 'Actual Date', value: inspection.actual_date ? `${formatDate(inspection.actual_date)} at ${formatTime(inspection.actual_date)}` : 'N/A' }
      ], y);

      y += 5;

      // Description
      if (inspection.description) {
        y = pdfUtils.addSectionHeader(doc, 'Description', y);
        y = pdfUtils.addTextBlock(doc, '', inspection.description, y);
        y += 5;
      }

      // Inspector Details
      y = pdfUtils.addSectionHeader(doc, 'Inspector Details', y);
      y = pdfUtils.addKeyValueGrid(doc, [
        { label: 'Inspector Name', value: inspection.inspector_name || 'Not assigned' },
        { label: 'Contact', value: inspection.inspector_contact || 'N/A' }
      ], y);

      y += 5;

      // Location & Work Details
      y = pdfUtils.addSectionHeader(doc, 'Location & Work Details', y);
      const locationDetails = [];
      if (inspection.location) locationDetails.push({ label: 'Location', value: inspection.location });
      if (inspection.work_area) locationDetails.push({ label: 'Work Area', value: inspection.work_area });
      if (inspection.contractor) locationDetails.push({ label: 'Contractor', value: inspection.contractor });
      if (inspection.phase) locationDetails.push({ label: 'Phase', value: inspection.phase });

      if (locationDetails.length > 0) {
        y = pdfUtils.addKeyValueGrid(doc, locationDetails, y);
      } else {
        y = pdfUtils.addTextBlock(doc, '', 'No location details provided', y);
      }

      y += 5;

      // Requirements
      if (inspection.requirements || inspection.safety_requirements || inspection.equipment_needed) {
        y = pdfUtils.addSectionHeader(doc, 'Requirements & Preparation', y);

        if (inspection.requirements) {
          y = pdfUtils.addTextBlock(doc, 'Inspection Requirements:', inspection.requirements, y);
        }

        if (inspection.safety_requirements) {
          y = pdfUtils.addTextBlock(doc, 'Safety Requirements:', inspection.safety_requirements, y);
        }

        if (inspection.equipment_needed) {
          y = pdfUtils.addTextBlock(doc, 'Equipment Needed:', inspection.equipment_needed, y);
        }

        y += 5;
      }

      // Inspection Results (if completed)
      if (inspection.result || inspection.pass_fail !== null && inspection.pass_fail !== undefined) {
        y = pdfUtils.addSectionHeader(doc, 'Inspection Results', y);

        const resultDetails = [];
        if (inspection.pass_fail !== null && inspection.pass_fail !== undefined) {
          resultDetails.push({ label: 'Result', value: inspection.pass_fail ? 'PASSED' : 'FAILED' });
        }

        if (resultDetails.length > 0) {
          y = pdfUtils.addKeyValueGrid(doc, resultDetails, y);
        }

        if (inspection.result) {
          y = pdfUtils.addTextBlock(doc, 'Findings:', inspection.result, y);
        }

        y += 5;
      }

      // Required Actions (if any)
      if (inspection.required_actions) {
        y = pdfUtils.addSectionHeader(doc, 'Required Actions', y);
        y = pdfUtils.addTextBlock(doc, '', inspection.required_actions, y);
        y += 5;
      }

      // Reinspection Date (if scheduled)
      if (inspection.reinspection_date) {
        y = pdfUtils.addSectionHeader(doc, 'Follow-up', y);
        y = pdfUtils.addKeyValueGrid(doc, [
          { label: 'Reinspection Date', value: formatDate(inspection.reinspection_date) }
        ], y);
        y += 5;
      }

      // Additional Notes
      if (inspection.notes) {
        y = pdfUtils.addSectionHeader(doc, 'Additional Notes', y);
        y = pdfUtils.addTextBlock(doc, '', inspection.notes, y);
      }

      // Export
      await pdfUtils.exportToPDF(
        doc,
        `Inspection_${inspection.inspection_number || inspection.inspection_id}_${new Date().toISOString().split('T')[0]}.pdf`,
        {
          title: 'Inspection Report',
          subtitle: inspection.title,
          documentNumber: inspection.inspection_number || inspection.inspection_id,
          date: formatDate(new Date().toISOString())
        }
      );

      showSuccess({
        title: 'Report Exported',
        message: 'Inspection report has been exported to PDF'
      });
    } catch (error: any) {
      console.error('Error exporting report:', error);
      showError({
        title: 'Export Failed',
        message: error?.message || 'Failed to export report to PDF'
      });
    }
  };

  const handleCancelInspection = async () => {
    try {
      const { inspectionService } = await import('../../../src/services/inspection.service');

      const response = await inspectionService.deleteInspection(inspection.inspection_id);

      if (response.success) {
        showSuccess({ title: 'Success', message: 'Inspection cancelled successfully' });
        onSuccess?.();
      } else {
        showError({ title: 'Error', message: 'Failed to cancel inspection' });
      }
    } catch (error: any) {
      console.error('Error cancelling inspection:', error);
      showError({ title: 'Error', message: error.message || 'Failed to cancel inspection' });
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm">
          <MoreVertical className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <ViewInspectionModal
          inspection={inspection}
          trigger={
            <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
              <Eye className="h-4 w-4 mr-2" />
              View Details
            </DropdownMenuItem>
          }
        />
        <InspectionReportModal
          inspection={inspection}
          onSuccess={onSuccess}
          trigger={
            <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
              <FileText className="h-4 w-4 mr-2" />
              Complete Report
            </DropdownMenuItem>
          }
        />
        <RescheduleInspectionModal
          inspection={inspection}
          onSuccess={onSuccess}
          trigger={
            <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
              <Calendar className="h-4 w-4 mr-2" />
              Reschedule
            </DropdownMenuItem>
          }
        />
        <DropdownMenuItem onClick={handleExportPDF}>
          <Download className="h-4 w-4 mr-2" />
          Export PDF
        </DropdownMenuItem>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <DropdownMenuItem className="text-destructive" onSelect={(e) => e.preventDefault()}>
              <Trash2 className="h-4 w-4 mr-2" />
              Cancel Inspection
            </DropdownMenuItem>
          </AlertDialogTrigger>
          <AlertDialogContent className="sm:max-w-[425px]">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <Trash2 className="h-5 w-5 text-destructive" />
                Cancel Inspection
              </AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to cancel this inspection: <strong>{inspection.title}</strong>? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>No, Keep It</AlertDialogCancel>
              <AlertDialogAction onClick={handleCancelInspection} className="bg-destructive hover:bg-destructive/90">
                Yes, Cancel Inspection
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function Inspections() {
  const [searchQuery, setSearchQuery] = useState("");
  const [projectFilter, setProjectFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [projects, setProjects] = useState<any[]>([]);
  const [inspections, setInspections] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Load projects and inspections
  useEffect(() => {
    loadProjects();
    loadInspections();
  }, []);

  const loadProjects = async () => {
    try {
      const response = await projectService.getProjects();
      if (response.success && response.data) {
        const projectData = Array.isArray(response.data)
          ? response.data
          : ((response.data as any).data || []);
        setProjects(projectData);
      }
    } catch (error) {
      console.error('Error loading projects for filter:', error);
    }
  };

  const loadInspections = async () => {
    setLoading(true);
    try {
      const { inspectionService } = await import('../../../src/services/inspection.service');
      const response = await inspectionService.getInspections(0, 1000);
      console.log('Inspections API Response:', response);

      if (response.success && response.data) {
        const inspectionData = Array.isArray(response.data)
          ? response.data
          : ((response.data as any).data || []);
        console.log('Setting inspections:', inspectionData);
        setInspections(inspectionData);
      }
    } catch (error) {
      console.error('Error loading inspections:', error);
      showError({ title: 'Error', message: 'Failed to load inspections' });
    } finally {
      setLoading(false);
    }
  };

  const oldInspections = [
    {
      id: "INS-001",
      title: "Foundation Concrete Pour Inspection",
      project: "Residential Complex Alpha",
      projectId: "residential-alpha",
      inspectionType: "pre-pour",
      status: "completed",
      priority: "high",
      requestedBy: "Site Manager",
      inspector: "John Doe - Senior Inspector",
      scheduledDate: "2024-03-10T08:00",
      completedDate: "2024-03-10T10:30",
      location: "Building A - Foundation",
      workArea: "Grid A-C, 1-5",
      contractor: "Main Contractor",
      phase: "foundation",
      result: "passed",
      findings: "Foundation layout and reinforcement verified. Ready for concrete pour.",
      deficiencies: "",
      followUpRequired: false,
      certificationIssued: true,
      overdue: false
    },
    {
      id: "INS-002",
      title: "Steel Frame Inspection - Phase 2",
      project: "Hospital Wing Expansion",
      projectId: "hospital-expansion",
      inspectionType: "steel-inspection",
      status: "scheduled",
      priority: "high",
      requestedBy: "Structural Engineer",
      inspector: "Mike Johnson - Structural Inspector",
      scheduledDate: "2024-03-15T14:00",
      completedDate: null,
      location: "Wing B - Levels 2-3",
      workArea: "Structural Steel Frame",
      contractor: "Steel Subcontractor",
      phase: "structure",
      result: null,
      findings: "",
      deficiencies: "",
      followUpRequired: false,
      certificationIssued: false,
      overdue: false
    },
    {
      id: "INS-003",
      title: "Electrical Rough-In Inspection",
      project: "Mixed-Use Development",
      projectId: "mixed-use",
      inspectionType: "electrical",
      status: "failed",
      priority: "medium",
      requestedBy: "Electrical Contractor",
      inspector: "Sarah Wilson - MEP Inspector",
      scheduledDate: "2024-03-08T09:00",
      completedDate: "2024-03-08T11:45",
      location: "Level 3 - Units 301-310",
      workArea: "Electrical Distribution",
      contractor: "Electrical Subcontractor",
      phase: "mep-rough",
      result: "failed",
      findings: "Multiple code violations found in panel installations",
      deficiencies: "Panel clearances insufficient, grounding issues identified",
      followUpRequired: true,
      certificationIssued: false,
      overdue: false
    },
    {
      id: "INS-004",
      title: "HVAC System Final Inspection",
      project: "School Renovation Project",
      projectId: "school-renovation",
      inspectionType: "hvac",
      status: "in-progress",
      priority: "medium",
      requestedBy: "HVAC Contractor",
      inspector: "Jane Smith - QA/QC Manager",
      scheduledDate: "2024-03-12T13:00",
      completedDate: null,
      location: "Gymnasium and Classrooms",
      workArea: "HVAC Systems",
      contractor: "HVAC Subcontractor",
      phase: "final",
      result: null,
      findings: "",
      deficiencies: "",
      followUpRequired: false,
      certificationIssued: false,
      overdue: false
    },
    {
      id: "INS-005",
      title: "Fire Safety Systems Test",
      project: "Industrial Warehouse",
      projectId: "industrial-warehouse",
      inspectionType: "fire-safety",
      status: "overdue",
      priority: "critical",
      requestedBy: "Safety Manager",
      inspector: "Alex Brown - Building Inspector",
      scheduledDate: "2024-03-05T10:00",
      completedDate: null,
      location: "Warehouse Floor - All Zones",
      workArea: "Fire Suppression System",
      contractor: "Fire Systems Contractor",
      phase: "final",
      result: null,
      findings: "",
      deficiencies: "",
      followUpRequired: false,
      certificationIssued: false,
      overdue: true
    }
  ];

  const filteredInspections = inspections.filter(inspection => {
    const matchesSearch = inspection.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         inspection.location?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (inspection.inspection_number || inspection.inspection_id)?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesProject = projectFilter === "all" || inspection.project_id === projectFilter;
    const matchesStatus = statusFilter === "all" || inspection.status === statusFilter;
    const matchesType = typeFilter === "all" || inspection.inspection_type === typeFilter;
    return matchesSearch && matchesProject && matchesStatus && matchesType;
  });

  const getStatusBadge = (status: string, overdue?: boolean) => {
    if (overdue) return <Badge variant="destructive">Overdue</Badge>;
    switch (status) {
      case "scheduled": return <Badge variant="secondary">Scheduled</Badge>;
      case "in-progress": return <Badge variant="default" className="bg-blue-600">In Progress</Badge>;
      case "completed": return <Badge variant="default" className="bg-green-600">Completed</Badge>;
      case "failed": return <Badge variant="destructive">Failed</Badge>;
      case "postponed": return <Badge variant="outline">Postponed</Badge>;
      case "cancelled": return <Badge variant="outline">Cancelled</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getResultBadge = (result: string) => {
    switch (result) {
      case "passed": return <Badge variant="default" className="bg-green-600">Passed</Badge>;
      case "passed-with-conditions": return <Badge variant="default" className="bg-yellow-600">Passed w/ Conditions</Badge>;
      case "failed": return <Badge variant="destructive">Failed</Badge>;
      case "incomplete": return <Badge variant="outline">Incomplete</Badge>;
      default: return null;
    }
  };

  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case "critical": return <Badge variant="destructive">Critical</Badge>;
      case "high": return <Badge className="bg-orange-600">High</Badge>;
      case "medium": return <Badge variant="secondary">Medium</Badge>;
      case "low": return <Badge variant="outline">Low</Badge>;
      default: return <Badge variant="outline">{priority}</Badge>;
    }
  };

  const getProjectName = (projectId: string) => {
    const project = projects.find(p => p.project_id === projectId);
    return project?.project_name || project?.name || 'Unknown Project';
  };

  const formatInspectionType = (type: string) => {
    return type?.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || '';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Inspections & QA/QC</h2>
          <p className="text-muted-foreground">Quality assurance and inspection management</p>
        </div>
        <NewInspectionModal onSuccess={loadInspections} />
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <ListChecks className="h-4 w-4 text-blue-600" />
              Total Inspections
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-700">{inspections.length}</div>
            <p className="text-xs text-blue-600 mt-1">All projects</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Calendar className="h-4 w-4 text-purple-600" />
              Scheduled
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-700">{inspections.filter(i => i.status === 'scheduled').length}</div>
            <p className="text-xs text-purple-600 mt-1">Upcoming inspections</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              Completed
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-700">{inspections.filter(i => i.status === 'completed').length}</div>
            <p className="text-xs text-green-600 mt-1">Finished inspections</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              Overdue/Failed
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-700">{inspections.filter(i => i.overdue || i.status === 'failed').length}</div>
            <p className="text-xs text-red-600 mt-1">Need attention</p>
          </CardContent>
        </Card>
      </div>

      {/* Inspection Management */}
      <Card>
        <CardHeader>
          <CardTitle>Inspection Register</CardTitle>
          <CardDescription>Manage quality inspections and reports</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex items-center gap-4 mb-6">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search inspections..."
                className="pl-9"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <Select value={projectFilter} onValueChange={setProjectFilter}>
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Projects</SelectItem>
                {projects.map((project) => (
                  <SelectItem key={project.project_id} value={project.project_id}>
                    {project.project_name || project.name || 'Unnamed Project'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="scheduled">Scheduled</SelectItem>
                <SelectItem value="in-progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="pre-pour">Pre-Pour</SelectItem>
                <SelectItem value="steel-inspection">Steel Work</SelectItem>
                <SelectItem value="electrical">Electrical</SelectItem>
                <SelectItem value="plumbing">Plumbing</SelectItem>
                <SelectItem value="hvac">HVAC</SelectItem>
                <SelectItem value="fire-safety">Fire Safety</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Inspection List */}
          <div className="space-y-4">
            {filteredInspections.map((inspection) => (
              <div key={inspection.inspection_id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="font-mono text-sm font-medium">{inspection.inspection_number || inspection.inspection_id}</span>
                      <h4 className="font-medium">{inspection.title}</h4>
                      {getStatusBadge(inspection.status)}
                      {inspection.priority && getPriorityBadge(inspection.priority)}
                      {inspection.result && getResultBadge(inspection.result)}
                      {inspection.pass_fail === true && <Badge variant="outline" className="bg-green-50 text-green-700">Passed</Badge>}
                      {inspection.pass_fail === false && <Badge variant="outline" className="bg-red-50 text-red-700">Failed</Badge>}
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-3">
                      <div>
                        <span className="font-medium">Project:</span>
                        <p className="text-muted-foreground">{getProjectName(inspection.project_id)}</p>
                      </div>
                      <div>
                        <span className="font-medium">Type:</span>
                        <p className="text-muted-foreground">{formatInspectionType(inspection.inspection_type)}</p>
                      </div>
                      <div>
                        <span className="font-medium">Inspector:</span>
                        <p className="text-muted-foreground">{inspection.inspector_name || 'Not assigned'}</p>
                      </div>
                      <div>
                        <span className="font-medium">Scheduled:</span>
                        <p className="text-muted-foreground">
                          {new Date(inspection.scheduled_date).toLocaleString()}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm mb-2">
                      {inspection.location && (
                        <div>
                          <span className="font-medium">Location:</span>
                          <p className="text-muted-foreground">{inspection.location}</p>
                        </div>
                      )}
                      {inspection.contractor && (
                        <div>
                          <span className="font-medium">Contractor:</span>
                          <p className="text-muted-foreground">{inspection.contractor}</p>
                        </div>
                      )}
                      {inspection.phase && (
                        <div>
                          <span className="font-medium">Phase:</span>
                          <p className="text-muted-foreground">{inspection.phase}</p>
                        </div>
                      )}
                    </div>

                    {inspection.description && (
                      <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <span className="font-medium text-blue-800 text-sm">Description: </span>
                        <span className="text-sm text-blue-700">{inspection.description}</span>
                      </div>
                    )}

                    {inspection.result && (
                      <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                        <span className="font-medium text-green-800 text-sm">Result: </span>
                        <span className="text-sm text-green-700">{inspection.result}</span>
                      </div>
                    )}

                    {inspection.required_actions && (
                      <div className="mt-2 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                        <span className="font-medium text-orange-800 text-sm">Required Actions: </span>
                        <span className="text-sm text-orange-700">{inspection.required_actions}</span>
                      </div>
                    )}

                    {inspection.reinspection_date && (
                      <div className="mt-2">
                        <Badge variant="outline" className="text-orange-700 border-orange-300">
                          Reinspection: {new Date(inspection.reinspection_date).toLocaleDateString()}
                        </Badge>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2 ml-4">
                    <ViewInspectionModal
                      inspection={inspection}
                      trigger={
                        <Button variant="outline" size="sm">
                          <Eye className="h-4 w-4" />
                        </Button>
                      }
                    />
                    {(inspection.status === 'scheduled' || inspection.status === 'in_progress') && (
                      <InspectionReportModal
                        inspection={inspection}
                        onSuccess={loadInspections}
                        trigger={
                          <Button variant="default" size="sm">
                            <FileText className="h-4 w-4 mr-1" />
                            Report
                          </Button>
                        }
                      />
                    )}
                    <InspectionActionsMenu inspection={inspection} onSuccess={loadInspections} projects={projects} />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredInspections.length === 0 && (
            <div className="text-center py-12">
              <Shield className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No inspections found</h3>
              <p className="text-muted-foreground">
                {searchQuery || projectFilter !== "all" || statusFilter !== "all" || typeFilter !== "all"
                  ? "Try adjusting your search criteria or filters"
                  : "Schedule your first inspection to get started"
                }
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Export all components
export { NewInspectionModal, ViewInspectionModal, InspectionReportModal, RescheduleInspectionModal, InspectionActionsMenu };
